// Session-scoped terminal availability for durable file references.
// Missing storage objects are not transient transport errors: once the
// backend or signed object URL proves a 404/410, every surface should render
// the reference as unavailable and selection logic must stop choosing it.

export interface UnavailableReferenceState {
    reason: string;
    observedAt: number;
    durable: boolean;
}

export interface FileReferenceHealthResourceCounts {
    unavailableReferenceEntries: number;
    durableUnavailableReferenceEntries: number;
    maximumUnavailableReferenceEntries: number;
}

export interface IngestManifestResult {
    files: Array<Record<string, unknown>>;
    s3Keys: string[];
}

const DEFAULT_UNAVAILABLE_TTL_MS = 5 * 60 * 1000;
const MAX_UNAVAILABLE_REFERENCES = 5000;
const unavailableReferences = new Map<string, UnavailableReferenceState>();

function normaliseReference(reference: unknown): string {
    return typeof reference === 'string' ? reference.trim() : '';
}

function rememberUnavailableReference(key: string, state: UnavailableReferenceState): void {
    // Refresh insertion order so references used by the current Project stay
    // resident while cold identities from much older Project visits retire.
    unavailableReferences.delete(key);
    unavailableReferences.set(key, state);
    while (unavailableReferences.size > MAX_UNAVAILABLE_REFERENCES) {
        const oldest = unavailableReferences.keys().next().value;
        if (oldest === undefined) break;
        unavailableReferences.delete(oldest);
    }
}

export function markFileReferenceUnavailable(
    reference: unknown,
    reason = 'cloud_object_missing',
    nowMs = Date.now(),
): void {
    const key = normaliseReference(reference);
    if (!key) return;
    rememberUnavailableReference(key, {
        reason: String(reason || 'cloud_object_missing'),
        observedAt: nowMs,
        durable: false,
    });
}

// Backend reconciliation is authoritative until a later backend response says
// `available` (or an explicit operator retry clears it). Unlike a direct
// browser observation, it must not silently expire after five minutes and
// restart the per-surface request cycle.
export function markDurableFileReferenceUnavailable(
    reference: unknown,
    reason = 'cloud_object_missing',
    nowMs = Date.now(),
): void {
    const key = normaliseReference(reference);
    if (!key) return;
    rememberUnavailableReference(key, {
        reason: String(reason || 'cloud_object_missing'),
        observedAt: nowMs,
        durable: true,
    });
}

export function clearFileReferenceUnavailable(reference: unknown): void {
    const key = normaliseReference(reference);
    if (key) unavailableReferences.delete(key);
}

export function fileReferenceUnavailableReason(
    reference: unknown,
    nowMs = Date.now(),
    ttlMs = DEFAULT_UNAVAILABLE_TTL_MS,
): string | null {
    const key = normaliseReference(reference);
    if (!key) return null;
    const state = unavailableReferences.get(key);
    if (!state) return null;
    if (!state.durable && nowMs - state.observedAt > ttlMs) {
        unavailableReferences.delete(key);
        return null;
    }
    rememberUnavailableReference(key, state);
    return state.reason;
}

/** Count-only diagnostic seam for long-session resource monitoring. */
export function getFileReferenceHealthResourceCounts(): FileReferenceHealthResourceCounts {
    let durable = 0;
    for (const state of unavailableReferences.values()) {
        if (state?.durable) durable += 1;
    }
    return {
        unavailableReferenceEntries: unavailableReferences.size,
        durableUnavailableReferenceEntries: durable,
        maximumUnavailableReferenceEntries: MAX_UNAVAILABLE_REFERENCES,
    };
}

export function candidateFileUnavailableReason(candidate: any): string | null {
    if (!candidate) return null;
    if (typeof candidate === 'string') return fileReferenceUnavailableReason(candidate);
    if (typeof candidate !== 'object') return null;
    if (candidate.unavailableReason) return String(candidate.unavailableReason);
    const availabilityState = candidate.availability_state || candidate.availabilityState;
    if (availabilityState && availabilityState !== 'available') {
        return String(
            candidate.availability_reason
            || candidate.availabilityReason
            || availabilityState,
        );
    }
    for (const reference of [
        candidate.s3Key,
        candidate.s3_key,
        candidate.pdfS3Key,
        candidate.pdf_s3_key,
        candidate.fileS3Key,
        candidate.file_s3_key,
        candidate.cloudObjectKey,
        candidate.cloud_object_key,
        candidate.fileMetadataId,
        candidate.file_metadata_id,
        candidate.id,
        candidate.entityId,
        candidate.entity_id,
    ]) {
        const reason = fileReferenceUnavailableReason(reference);
        if (reason) return reason;
    }
    return null;
}

function manifestRowString(row: Record<string, unknown> | null | undefined, ...keys: string[]): string {
    for (const key of keys) {
        const value = row?.[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
}

function manifestRowReferences(row: Record<string, unknown>): string[] {
    return Array.from(new Set([
        manifestRowString(row, 'referenceValue', 'reference_value'),
        manifestRowString(row, 's3Key', 's3_key'),
        manifestRowString(row, 'fileMetadataId', 'file_metadata_id', 'id'),
    ].filter(Boolean)));
}

/**
 * Project-list wire adapter shared by project-open reconciliation and the
 * signed-URL hook. Synthetic missing-metadata rows deliberately have no S3
 * key; their canonical reference_value/file_metadata_id still becomes a
 * durable session signal without inventing a storage locator. `entity_id` is
 * deliberately excluded: it is only Project-local (many Projects legitimately
 * contain `photo-1`) and this cache is session-global. Canonical S3 keys and
 * file-metadata UUIDs are the cross-Project-safe identities.
 */
export function ingestProjectFileHealthManifest(payload: unknown): IngestManifestResult {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return { files: [], s3Keys: [] };
    }
    const candidateFiles = (payload as Record<string, unknown>).files;
    if (!Array.isArray(candidateFiles)) return { files: [], s3Keys: [] };

    const files = candidateFiles.filter(row => (
        !!row && typeof row === 'object' && !Array.isArray(row)
    )) as Array<Record<string, unknown>>;
    const s3Keys: string[] = [];
    const seenKeys = new Set<string>();
    for (const row of files) {
        const state = manifestRowString(row, 'availabilityState', 'availability_state');
        const reason = manifestRowString(row, 'availabilityReason', 'availability_reason') || state;
        const references = manifestRowReferences(row);
        if (state && state !== 'available') {
            for (const reference of references) {
                markDurableFileReferenceUnavailable(reference, reason);
            }
            continue;
        }
        if (state === 'available') {
            for (const reference of references) clearFileReferenceUnavailable(reference);
        }
        const s3Key = manifestRowString(row, 's3Key', 's3_key');
        if (s3Key && !seenKeys.has(s3Key)) {
            seenKeys.add(s3Key);
            s3Keys.push(s3Key);
        }
    }
    return { files, s3Keys };
}

export function __resetFileReferenceHealthForTests(): void {
    unavailableReferences.clear();
}
